import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Avatar } from "../shared/Avatar";
import { PhotoUploader } from "../shared/PhotoUploader";
import { InfoItem } from "../shared/InfoItem";
import { CustomIcon } from "../shared/CustomIcon";
import { Card, Button, Pill, PillRow, Badge, StatusRing, EmptyState } from "../shared";
import { fullName, formatDate, formatMonthDay, age, uid, parseDateOnly, formatMemberNumber } from "../../lib/format";
import { apiListDepartments } from "../departments";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";
import { canViewMemberField, PRIVACY_DEFAULTS } from "../../lib/memberPrivacy";
import MemberPrivacyEditor from "../shared/MemberPrivacyEditor";
import {
  MembersFilterPanel,
  SortDropdown,
  SORT_OPTIONS,
  ActiveFilterSummary,
  memberMatchesStatus,
  memberMatchesSpiritual,
  memberMatchesFamily,
  memberMatchesFollowUp,
  memberMatchesBirthdayMonth,
} from "./MembersFilterPanel";
import { FamiliesPage } from "../families";
import MemberProfileExtras from "./MemberProfileExtras";
import { FlockGroupTabs } from "./FlockGroupTabs";
import { FlockToolbarMenu } from "./FlockToolbarMenu";
import { FlockFilterPills } from "./FlockFilterPills";
import { FlockMemberCard } from "./FlockMemberCard";
import { TransferModal } from "./TransferModal";
import { TreePlacementPrompt } from "./TreePlacementPrompt";

// Per-record API helpers (Protection 7 — never bulk-overwrites kc_members)
async function apiCreateMember(member) {
  const res = await kcFetch(`${API_BASE}/members`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(member),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Create failed");
  return res.json();
}
async function apiUpdateMember(id, patch) {
  const res = await kcFetch(`${API_BASE}/members/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Update failed");
  return res.json();
}
async function apiDeleteMember(id) {
  const res = await kcFetch(`${API_BASE}/members/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Delete failed");
  return res.json();
}

function MembersPage({ currentUser, isSuperAdmin, members, setMembers, families, setFamilies, users, departments, visibleMembers, addAudit, addNotification, persist, dataLoaded = true }) {
  const [search, setSearch] = useState("");
  const [statusChip, setStatusChip] = useState("All"); // quick filter chip selection (within a group tab)
  const [activeTab, setActiveTab] = useState("members"); // members | families (top-level)
  // Iter 86 — group tab under The Flock: members | visitors | guests | all
  // Always defaults to "members" on every page load (do NOT persist).
  const [groupTab, setGroupTab] = useState("members");
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [sortBy, setSortBy] = useState("name-asc");
  // Iter 86 — Super Admin bulk-transfer selection state.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferBusy, setTransferBusy] = useState(false);
  const [treePlacementQueue, setTreePlacementQueue] = useState([]); // member ids just transferred to Member
  const [toast, setToast] = useState("");
  const toastTimerRef = useRef(null);
  const showToast = (msg) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(""), 3500);
  };
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);
  // Exit selection mode + clear selection when the group tab changes.
  useEffect(() => { setSelectMode(false); setSelectedIds(new Set()); }, [groupTab]);
  const [filters, setFilters] = useState({
    gender: "All",
    statuses: [],
    marital: [],
    languages: [],
    departments: [],
    spiritual: [],
    family: null,
    followup: null,
    birthdayMonths: [],
  });
  const [showModal, setShowModal] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const isAdmin = currentUser?.role === "superadmin" || currentUser?.role === "limited_admin";
  // Live department records (the new schema with leaderMemberId) — used to
  // render the auto Department-Leader badges on member profiles. Reloads on
  // mount and after this page navigates back, so newly assigned leaders show
  // up immediately without a hard page refresh.
  const [departmentRecords, setDepartmentRecords] = useState([]);
  useEffect(() => { apiListDepartments().then(d => setDepartmentRecords(Array.isArray(d) ? d : [])).catch(() => {}); }, []);
  const { id: paramId } = useParams();
  const reactNav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ?new=1 → auto-open Add Member modal once. Consume the param so back/forward
  // don't re-trigger the modal after the user closes it.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setEditMember(null);
      setShowModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ?chip=<Status> → pre-select the Flock pill filter once (Iter-74 KPI deep links).
  // Consumed on mount so back/forward doesn't keep resetting the user's later choice.
  useEffect(() => {
    const chip = searchParams.get("chip");
    if (chip && ["All", "Active", "New Member", "Visitor", "Guest", "Inactive", "Left Church", "Deceased"].includes(chip)) {
      setStatusChip(chip);
      const next = new URLSearchParams(searchParams);
      next.delete("chip");
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derive viewMember directly from URL — back/forward buttons "just work"
  const viewMember = paramId || null;
  const openMember = (id) => reactNav(`/members/${id}`);
  const closeMember = () => reactNav(`/members`);

  // ----- Derived lists for the filter panel -----
  const availableLanguages = useMemo(() => {
    const set = new Set();
    members.forEach(m => { if (m && !m.masked && m.language) set.add(String(m.language).trim()); });
    return Array.from(set).filter(Boolean).sort();
  }, [members]);
  const availableDepartments = useMemo(() => {
    const set = new Set();
    members.forEach(m => (m.departments || []).forEach(d => d && set.add(d)));
    (departments || []).forEach(d => d && set.add(typeof d === "string" ? d : d.name));
    return Array.from(set).filter(Boolean).sort();
  }, [members, departments]);

  // Iter 86 — first-pass filter by GROUP tab (Members / Visitors / Guests / All).
  // The Members tab includes every "resident" status; Visitors and Guests are
  // single-status groups; All is unfiltered.
  const MEMBER_GROUP_STATUSES = ["Active", "New Member", "Inactive", "Deceased", "Left Church"];
  const groupOf = (m) => {
    const st = m?.memberStatus || "";
    if (st === "Visitor") return "visitors";
    if (st === "Guest") return "guests";
    return MEMBER_GROUP_STATUSES.includes(st) ? "members" : "members"; // unknown → assume member
  };
  const inCurrentGroup = (m) => {
    if (groupTab === "all") return true;
    const st = m?.memberStatus || "";
    // Iter 86e — enforce a single content definition of each group tab for
    // every role. Masked rows must obey the same status predicate as
    // unmasked rows; otherwise Pastor / Attendance-Authority Team Members
    // (whose non-assigned rows arrive masked) would see masked Visitors
    // and Guests leak into the Members tab, making it behave like All.
    if (groupTab === "members") return MEMBER_GROUP_STATUSES.includes(st);
    if (groupTab === "visitors") return st === "Visitor";
    if (groupTab === "guests") return st === "Guest";
    return true;
  };

  // ----------------------------------------------------------------
  // Iter 86e — Role-scoped tab visibility.
  // Rule matrix (from user spec):
  //   • SA / LA / Pastor / TM with attendanceAccess → full ["members","visitors","guests","all"].
  //   • TM without attendanceAccess who has one or more Visitors in their
  //     assignedMembers list → ["members","visitors"] only, and the Visitors
  //     tab is scoped client-side to those assignments as well (backend also
  //     drops non-assigned visitors + all guests for this role, but we belt-
  //     and-suspenders here in case of stale cached data).
  //   • Everyone else (plain TM, no assignments) → ["members"] only.
  // ----------------------------------------------------------------
  const role = currentUser?.role;
  const attendanceAccess = currentUser?.attendanceAccess === true;
  const assignedIds = useMemo(() => new Set(Array.isArray(currentUser?.assignedMembers) ? currentUser.assignedMembers : []), [currentUser]);
  const hasVisitorAssignments = useMemo(() => {
    if (!assignedIds.size) return false;
    return (members || []).some(m => m && m.memberStatus === "Visitor" && assignedIds.has(m.id));
  }, [members, assignedIds]);
  const visibleTabs = useMemo(() => {
    if (role === "superadmin" || role === "limited_admin" || role === "pastor" || (role === "team_member" && attendanceAccess)) {
      return ["members", "visitors", "guests", "all"];
    }
    if (role === "team_member" && hasVisitorAssignments) return ["members", "visitors"];
    return ["members"];
  }, [role, attendanceAccess, hasVisitorAssignments]);
  const isRestrictedTM = role === "team_member" && !attendanceAccess;

  // Snap the current group tab back to a visible one whenever the visible
  // set shrinks (e.g. an admin was demoted or lost their visitor assignments).
  useEffect(() => {
    if (!visibleTabs.includes(groupTab)) setGroupTab(visibleTabs[0] || "members");
  }, [visibleTabs, groupTab]);

  // ----- Combined filter pipeline -----
  // Directory shows ALL members — masked rows surface only the name.
  // Any active advanced filter (other than search) skips masked rows.
  const filtered = useMemo(() => {
    const anyAdvanced =
      (filters.gender && filters.gender !== "All") ||
      filters.statuses.length || filters.marital.length || filters.languages.length ||
      filters.departments.length || filters.spiritual.length ||
      filters.family || filters.followup || filters.birthdayMonths.length ||
      statusChip !== "All";

    return members.filter(m => {
      // Iter 86 — enforce the current group tab first.
      if (!inCurrentGroup(m)) return false;
      // Iter 86e — restricted TM: scope Visitors to their assigned ids only.
      if (isRestrictedTM && (m?.memberStatus === "Visitor") && !assignedIds.has(m.id)) return false;
      if (isRestrictedTM && (m?.memberStatus === "Guest")) return false;
      const phoneFirst = Array.isArray(m.phone) ? (m.phone[0]?.number || "") : "";
      const q = search.trim().toLowerCase();
      // Member number match: pad the stored value to 7 digits so the user can
      // type either the raw number ("1") OR the fully-padded form ("0000001").
      // We deliberately do NOT strip leading zeros from the padded form — that
      // way "0000001" matches ONLY #0000001 (not #0000010 or #0000017), while
      // typing just "1" still matches everything containing a 1.
      const rawStored = String(m.memberNumber || "").replace(/^#/, "");
      const paddedStored = rawStored && /^\d+$/.test(rawStored) ? rawStored.padStart(7, "0") : rawStored;
      const qNum = q.replace(/^#/, "");
      const matchNumber = !!qNum && !!paddedStored && paddedStored.includes(qNum);
      const matchSearch = !q
        || fullName(m).toLowerCase().includes(q)
        || phoneFirst.toLowerCase().includes(q)
        || matchNumber;
      if (!matchSearch) return false;
      if (m.masked) return !anyAdvanced; // masked rows only match search
      // Quick chip — Phase 5B: direct memberStatus equality
      // (labels: Active, New Member, Visitor, Guest, Inactive, Left Church, Deceased)
      if (statusChip !== "All" && (m.memberStatus || "") !== statusChip) return false;
      // Gender (secondary, AND with all)
      if (filters.gender && filters.gender !== "All" && m.gender !== filters.gender) return false;
      // Multi-status (in addition to quick chip)
      if (filters.statuses.length && !filters.statuses.some(s => memberMatchesStatus(m, s))) return false;
      // Marital
      if (filters.marital.length && !filters.marital.includes(m.maritalStatus)) return false;
      // Language
      if (filters.languages.length && !filters.languages.includes(m.language)) return false;
      // Departments (member must be in at least one of selected)
      if (filters.departments.length && !(m.departments || []).some(d => filters.departments.includes(d))) return false;
      // Spiritual (member must match all selected — they're cumulative checks)
      if (filters.spiritual.length && !filters.spiritual.every(s => memberMatchesSpiritual(m, s))) return false;
      // Family
      if (filters.family && !memberMatchesFamily(m, filters.family, families)) return false;
      // Follow-up
      if (filters.followup && !memberMatchesFollowUp(m, filters.followup, users)) return false;
      // Birthday month
      if (filters.birthdayMonths.length && !memberMatchesBirthdayMonth(m, filters.birthdayMonths)) return false;
      return true;
    });
  }, [members, search, statusChip, filters, families, users, groupTab]);

  // ----- Sort -----
  const sorted = useMemo(() => {
    const arr = [...filtered];
    // Both A→Z and Z→A now use Last-Name-then-First-Name keys, matching the
    // global `sortMembersByName` used by every dropdown in the app, so the
    // master Members list is consistent with selectors elsewhere.
    const cmpAsc = (a, b) => {
      const la = (a.lastName || "").toLowerCase();
      const lb = (b.lastName || "").toLowerCase();
      if (la !== lb) return la.localeCompare(lb, undefined, { sensitivity: "base" });
      const fa = (a.firstName || "").toLowerCase();
      const fb = (b.firstName || "").toLowerCase();
      return fa.localeCompare(fb, undefined, { sensitivity: "base" });
    };
    const todayIdx = (() => { const d = new Date(); return d.getMonth() * 31 + d.getDate(); })();
    const bdayDist = (m) => {
      if (!m.dob) return Infinity;
      const d = parseDateOnly(m.dob);
      if (!d || isNaN(d.getTime())) return Infinity;
      const idx = d.getMonth() * 31 + d.getDate();
      const diff = idx - todayIdx;
      return diff < 0 ? diff + 372 : diff;
    };
    switch (sortBy) {
      case "name-desc":  arr.sort((a, b) => -cmpAsc(a, b)); break;
      case "joined-desc": arr.sort((a, b) => String(b.joinedDate || "").localeCompare(String(a.joinedDate || ""))); break;
      case "joined-asc":  arr.sort((a, b) => String(a.joinedDate || "9999").localeCompare(String(b.joinedDate || "9999"))); break;
      case "birthday":   arr.sort((a, b) => bdayDist(a) - bdayDist(b)); break;
      case "department": arr.sort((a, b) => String((a.departments || [])[0] || "").localeCompare(String((b.departments || [])[0] || ""))); break;
      case "name-asc":
      default:           arr.sort(cmpAsc); break;
    }
    return arr;
  }, [filtered, sortBy]);

  // Mirror a member's new familyId onto local `families` state — keeps the
  // family card in sync without a full refetch when the user edits a member
  // and changes their Family Unit dropdown.
  const cascadeFamilyOnSave = (memberId, newFamilyId) => {
    if (typeof setFamilies !== "function") return;
    setFamilies(prev => prev.map(f => {
      if (!f) return f;
      const has = Array.isArray(f.members) && f.members.includes(memberId);
      if (f.id === newFamilyId) {
        if (has) return f;
        return { ...f, members: [...(f.members || []), memberId] };
      }
      if (has) {
        return { ...f, members: (f.members || []).filter(x => x !== memberId) };
      }
      return f;
    }));
  };

  const saveMember = async (data) => {
    try {
      if (data.id) {
        const fresh = await apiUpdateMember(data.id, data);
        const updated = members.map(m => m.id === data.id ? fresh : m);
        setMembers(updated);
        cascadeFamilyOnSave(fresh.id, fresh.familyId || null);
        addAudit(currentUser.id, currentUser.name, "Updated member", `Updated ${fullName(data)}`);
      } else {
        const draft = { ...data, id: uid() };
        const fresh = await apiCreateMember(draft);
        const updated = [...members, fresh];
        setMembers(updated);
        if (fresh.familyId) cascadeFamilyOnSave(fresh.id, fresh.familyId);
        addAudit(currentUser.id, currentUser.name, "Created member", `Added ${fullName(fresh)}`);
        addNotification(`New member added: ${fullName(fresh)}`, "general", { memberId: fresh.id });
      }
      setShowModal(false); setEditMember(null);
    } catch (err) {
      alert(`Save failed: ${err.message || err}`);
    }
  };

  const deleteMember = async (id) => {
    const m = members.find(x => x.id === id);
    if (!window.confirm(`Delete ${fullName(m)}? This cannot be undone.`)) return;
    try {
      await apiDeleteMember(id);
      const updated = members.filter(x => x.id !== id);
      setMembers(updated);
      addAudit(currentUser.id, currentUser.name, "Deleted member", `Removed ${fullName(m)}`);
      closeMember();
    } catch (err) {
      alert(`Delete failed: ${err.message || err}`);
    }
  };

  if (viewMember) {
    const m = members.find(x=>x.id===viewMember);
    if (!m) {
      // ID in URL doesn't match any member — show graceful 404 view, NOT auto-redirect
      return (
        <div data-testid="member-not-found" className="empty" style={{padding:"60px 20px"}}>
          <div className="empty-icon"><CustomIcon name="people" size={40} /></div>
          <div style={{fontFamily:"'Cinzel',serif",fontSize:22,color:"var(--kc-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Member Not Found</div>
          <div className="empty-text" style={{marginBottom:18}}>No member exists with id <strong style={{color:"var(--kc-accent)"}}>{viewMember}</strong>.</div>
          <button className="btn btn-gold" onClick={closeMember}>← Back to The Flock</button>
        </div>
      );
    }
    if (m.masked) {
      // Pastor / Team Member viewing a member NOT assigned to them — only name + photo.
      return (
        <div data-testid={`member-masked-${m.id}`}>
          <div className="flex items-center gap-8 mb-20 flex-wrap">
            <button className="btn btn-outline" onClick={closeMember}>← Back</button>
          </div>
          <div className="profile-hero">
            <Avatar className="profile-avatar-lg" name={fullName(m)} photo={m.photo} status={m.memberStatus}/>
            <div>
              <div className="profile-name">{fullName(m)}</div>
              <div className="profile-meta" style={{ color: "var(--kc-sub)" }}>Profile details restricted</div>
            </div>
          </div>
          <div className="card" style={{ padding: 20, marginTop: 18, borderLeft: "3px solid color-mix(in srgb, var(--kc-accent) 55%, transparent)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 18, display: "inline-flex", alignItems: "center" }}><CustomIcon name="lock" size={18} /></span>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 14, color: "var(--kc-accent)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>Restricted Profile</div>
            </div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "var(--kc-sub)", lineHeight: 1.6 }}>
              You can only see <strong>{fullName(m)}</strong>&apos;s name. Full profile details (contact info, family, departments, spiritual background) are visible only to assigned team members. Ask a Super Admin in <em>Users &amp; Roles</em> to assign this member to you if you need access.
            </div>
          </div>
        </div>
      );
    }
    const inviter = m.invitedBy ? members.find(x=>x.id===m.invitedBy) : null;
    const family = m.familyId ? families.find(x=>x.id===m.familyId) : null;
    return (
      <div>
        <div className="flex items-center gap-8 mb-20 flex-wrap">
          <button className="btn btn-outline" onClick={closeMember}>← Back</button>
          {isSuperAdmin&&<button className="btn btn-primary" onClick={()=>{setEditMember(m);setShowModal(true);}} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="edit" size={14} /> Edit</button>}
          {isSuperAdmin&&<button className="btn btn-danger" onClick={()=>deleteMember(m.id)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="trash" size={14} /> Delete</button>}
          <button className="btn btn-outline" onClick={()=>window.print()} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="printer" size={14} /> Print Profile</button>
        </div>
        <div className="profile-hero">
          <Avatar className="profile-avatar-lg" name={fullName(m)} photo={m.photo} status={m.memberStatus}/>
          <div>
            <div className="profile-name">{fullName(m)}</div>
            <div className="profile-meta">{m.memberStatus} · {(m.departments || []).join(", ")||"No department"}</div>
            <div style={{marginTop:10,display:"flex",gap:6,flexWrap:"wrap"}}>
              {m.tags?.map(t=><span key={t} className="badge badge-gold">{t}</span>)}
            </div>
            {(isSuperAdmin || currentUser?.role === "limited_admin") && (() => {
              // Find the user (team_member / pastor) who has this member in their assigned list.
              const followedBy = (users || []).find(u => Array.isArray(u.assignedMembers) && u.assignedMembers.includes(m.id) && (u.role === "team_member" || u.role === "pastor"));
              if (!followedBy) return null;
              return (
                <div
                  data-testid="member-followed-by"
                  style={{
                    marginTop: 14, display: "inline-flex", alignItems: "center", gap: 10,
                    padding: "6px 12px 6px 6px", borderRadius: 999,
                    background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
                    border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
                  }}
                >
                  <Avatar name={followedBy.name} photo={followedBy.photo} memberId={followedBy.memberId} style={{ width: 28, height: 28, fontSize: 11 }} />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--kc-accent)", fontFamily: "'Cinzel',serif" }}>
                      Followed by
                    </span>
                    <span style={{ fontSize: 12, color: "var(--kc-text)", fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>{followedBy.name}</span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          {(() => {
            // Privacy gate per field. canViewMemberField returns true for SA/LA,
            // the member themselves, the assigned Pastor, OR if the field's
            // privacy flag is true (default visible).
            const canSee = (key) => canViewMemberField(currentUser, m, key, users);
            const showBirthYear = canSee("birthYear");
            // Birth date: always show month/day. Year is hidden unless privacy allows.
            const dobLabel = m.dob ? (showBirthYear ? formatDate(m.dob) : formatMonthDay(m.dob)) : "—";
            return (
          <>
          <div className="card" style={{padding:18}}>
            <div className="form-section-title">Personal Info</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {(() => {
                if (m.memberNumber) {
                  return <InfoItem label="Member Number" value={formatMemberNumber(m.memberNumber)} />;
                }
                return (
                  <div data-testid="member-info-number-empty" style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontFamily: "'Cinzel',serif", fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
                      Member Number
                    </div>
                    <div style={{ fontSize: 12, color: "var(--kc-sub)", fontStyle: "italic", marginTop: 2 }}>
                      Not yet assigned — number will be assigned upon tree placement.
                    </div>
                  </div>
                );
              })()}
              <InfoItem label="Date of Birth" value={dobLabel}/>
              {showBirthYear && <InfoItem label="Age" value={age(m.dob)}/>}
              {canSee("gender") && <InfoItem label="Gender" value={m.gender}/>}
              {canSee("maritalStatus") && <InfoItem label="Marital Status" value={m.maritalStatus}/>}
              {canSee("language") && <InfoItem label="Language" value={m.language}/>}
              <InfoItem label="Joined" value={formatDate(m.joinedDate)}/>
              {canSee("profession") && m.profession && <InfoItem label="Profession" value={m.profession}/>}
              {canSee("profession") && m.professionDetails && <InfoItem label="Profession Details" value={m.professionDetails}/>}
            </div>
          </div>
          <div className="card" style={{padding:18}}>
            <div className="form-section-title">Contact</div>
            {canSee("mobilePhone") && (m.phone || []).map((p,i)=><InfoItem key={`phone-${i}-${p.number||p.label}`} label={p.label+" Phone"} value={p.number}/>)}
            {canSee("mobilePhone") && !(m.phone && m.phone.length) && m.mobilePhone && <InfoItem label="Mobile Phone" value={m.mobilePhone}/>}
            {canSee("personalEmail") && (m.email || []).map((e,i)=><InfoItem key={`email-${i}-${e.address||e.label}`} label={e.label+" Email"} value={e.address}/>)}
            {canSee("personalEmail") && !(m.email && m.email.length) && m.personalEmail && <InfoItem label="Personal Email" value={m.personalEmail}/>}
            {canSee("physicalAddress") && <InfoItem label="Address" value={m.address}/>}
          </div>
          <div className="card" style={{padding:18}}>
            <div className="form-section-title">Spiritual Background</div>
            {canSee("baptism") && (
              <>
                <InfoItem label="Baptized" value={m.baptized?"Yes":"No"}/>
                {m.baptized&&<><InfoItem label="Baptized By" value={m.baptizedBy}/><InfoItem label="Where" value={m.baptizedWhere}/><InfoItem label="Date" value={formatDate(m.baptizedDate)}/></>}
              </>
            )}
            <InfoItem label="Background" value={m.spiritualBackground}/>
          </div></>
            );
          })()}
          <div className="card" style={{padding:18}}>
            <div className="form-section-title">Church Info</div>
            <InfoItem label="Departments" value={(m.departments || []).join(", ")||"None"}/>
            {(() => {
              // Find user(s) linked to this member, then find depts they lead.
              const linkedUserIds = (users || []).filter(u => u.memberId === m.id).map(u => u.id);
              const ledDepts = departmentRecords.filter(d => d && d.leaderUserId && linkedUserIds.includes(d.leaderUserId));
              if (ledDepts.length === 0) return null;
              return (
                <div data-testid={`member-leader-badges-${m.id}`} style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "8px 0 4px" }}>
                  {ledDepts.map(d => (
                    <span
                      key={d.id}
                      data-testid={`member-leader-badge-${d.id}`}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: "color-mix(in srgb, var(--kc-accent) 14%, transparent)",
                        border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)",
                        color: "var(--kc-accent)",
                        fontFamily: "'Montserrat', sans-serif", fontWeight: 600,
                        fontSize: 11, letterSpacing: "0.04em",
                        padding: "5px 10px", borderRadius: 999,
                      }}
                      title={`Department Leader — ${d.name}`}
                    >
                      <CustomIcon name="crown" size={12} />
                      <span style={{ textTransform: "uppercase", fontSize: 9, letterSpacing: "0.08em", fontWeight: 700 }}>Department Leader</span>
                      <span style={{ opacity: 0.85 }}>·</span>
                      <span>{d.name}</span>
                    </span>
                  ))}
                </div>
              );
            })()}
            <InfoItem label="Family" value={family?family.name:"None"}/>
            {canViewMemberField(currentUser, m, "invitedBy", users) && <InfoItem label="Invited By" value={inviter?fullName(inviter):"—"}/>}
            {canViewMemberField(currentUser, m, "additionType", users) && <InfoItem label="Addition Type" value={m.additionType}/>}
            {m.subtractionType&&<InfoItem label="Departure" value={`${m.subtractionType}${m.subtractionDate?" — "+formatDate(m.subtractionDate):""}`}/>}
            {m.subtractionComment&&<InfoItem label="Departure Notes" value={m.subtractionComment}/>}
          </div>
        </div>
        <MemberProfileExtras member={m} currentUser={currentUser} />
        {showModal&&<MemberModal member={editMember} members={members} families={families} departments={departments} currentUser={currentUser} onSave={saveMember} onClose={()=>{setShowModal(false);setEditMember(null);}}/>}
      </div>
    );
  }

  return (
    <div>
      {/* Tabs — Members / Families */}
      <div data-testid="members-tabs" style={{ display: "flex", gap: 4, borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)", marginBottom: 14, overflowX: "auto" }}>
        <button
          type="button"
          data-testid="members-tab-members"
          onClick={() => setActiveTab("members")}
          style={{
            padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer",
            fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: activeTab === "members" ? "var(--kc-accent)" : "var(--kc-sub)",
            borderBottom: activeTab === "members" ? "2px solid var(--kc-accent)" : "2px solid transparent",
            whiteSpace: "nowrap",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        ><CustomIcon name="people" size={14} /> The Flock</button>
        <button
          type="button"
          data-testid="members-tab-families"
          onClick={() => setActiveTab("families")}
          style={{
            padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer",
            fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: activeTab === "families" ? "var(--kc-accent)" : "var(--kc-sub)",
            borderBottom: activeTab === "families" ? "2px solid var(--kc-accent)" : "2px solid transparent",
            whiteSpace: "nowrap",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        ><CustomIcon name="family" size={14} /> Families</button>
      </div>

      {activeTab === "families" ? (
        <FamiliesPage
          isSuperAdmin={isSuperAdmin}
          families={families}
          setFamilies={setFamilies}
          members={members}
          setMembers={setMembers}
          addAudit={addAudit}
          currentUser={currentUser}
          persist={persist}
        />
      ) : (
      <>
      {/* Top toolbar — compact single row: search + add + kebab overflow */}
      <div className="flex items-center mb-12 gap-8" style={{ flexWrap: "nowrap" }}>
        <div className="search-bar" style={{ maxWidth: 380, flex: "1 1 200px", minWidth: 140 }}>
          <span><CustomIcon name="search" size={14} /></span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" data-testid="members-search" />
        </div>
        {isSuperAdmin && (
          <button
            type="button"
            data-testid="add-member-btn"
            onClick={() => { setEditMember(null); setShowModal(true); }}
            aria-label="Add member"
            title="Add member"
            style={{
              flex: "0 0 auto",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
              padding: "7px 12px", borderRadius: 999,
              background: "var(--kc-accent)", color: "#111",
              border: "none", cursor: "pointer", fontWeight: 800, fontFamily: "'Montserrat',sans-serif",
              fontSize: 15, lineHeight: 1,
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 16, fontWeight: 800, marginTop: -1 }}>+</span>
            <CustomIcon name="people" size={16} />
          </button>
        )}
        {isSuperAdmin && selectMode && (
          <span
            data-testid="members-select-count"
            style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 12,
              padding: "6px 10px", borderRadius: 999,
              background: "color-mix(in srgb, var(--kc-accent) 14%, transparent)",
              color: "var(--kc-accent)", fontWeight: 700,
              flex: "0 0 auto",
            }}
          >
            {selectedIds.size} selected
          </span>
        )}
        <FlockToolbarMenu
          isSuperAdmin={isSuperAdmin}
          selectMode={selectMode}
          allSelected={
            selectMode
              ? sorted.filter(m => !m.masked).length > 0
                && sorted.filter(m => !m.masked).every(m => selectedIds.has(m.id))
              : false
          }
          sortBy={sortBy}
          onFilter={() => setFilterPanelOpen(true)}
          onSort={(id) => setSortBy(id)}
          onEnterSelect={() => { setSelectMode(true); setSelectedIds(new Set()); }}
          onExitSelect={() => { setSelectMode(false); setSelectedIds(new Set()); }}
          onSelectAllToggle={() => {
            const selectable = sorted.filter(m => !m.masked);
            const allOn = selectable.length > 0 && selectable.every(m => selectedIds.has(m.id));
            setSelectedIds(allOn ? new Set() : new Set(selectable.map(m => m.id)));
          }}
        />
      </div>

      {/* Iter 86 — Group tabs: Members / Visitors / Guests / All */}
      <FlockGroupTabs
        active={groupTab}
        onChange={setGroupTab}
        visibleTabs={visibleTabs}
      />

      {/* Iter 86c/e — sub-filter rows removed for both Members and All tabs.
          Both tabs now show a flat filtered list; users use the kebab
          → Filter modal for advanced filtering. */}

      {/* Active filter summary tags (block flow, no overlay) */}
      <ActiveFilterSummary filters={filters} setFilters={setFilters} isAdmin={isAdmin} />

      {/* Result count */}
      <div
        data-testid="members-count"
        className="text-meta"
        style={{ marginTop: 8, marginBottom: 12 }}
      >
        Showing {sorted.length} of {members.length} members
      </div>

      <MembersFilterPanel
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        filters={filters}
        setFilters={setFilters}
        availableLanguages={availableLanguages}
        availableDepartments={availableDepartments}
        isAdmin={isAdmin}
      />

      {/* Members list — semantic Card rows (Phase 5B) */}
      {!dataLoaded ? (
        <div data-testid="members-loading" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[0,1,2,3,4].map(i => (
            <div key={i} className="card" style={{ padding: 14, opacity: 0.55 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "color-mix(in srgb, var(--kc-text) 10%, transparent)" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ height: 12, width: "40%", background: "color-mix(in srgb, var(--kc-text) 10%, transparent)", borderRadius: 4, marginBottom: 6 }} />
                  <div style={{ height: 10, width: "25%", background: "color-mix(in srgb, var(--kc-text) 8%, transparent)", borderRadius: 4 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <EmptyState
          data-testid="members-empty"
          icon="people"
          title={search || Object.keys(filters || {}).length ? "No members match your filters" : "No members yet"}
          subtitle={search || Object.keys(filters || {}).length
            ? "Try clearing filters or searching for a different name."
            : "Add your first member to start building the flock."}
          action={isSuperAdmin && !search && !Object.keys(filters || {}).length ? {
            label: "+ Add Member",
            onClick: () => { setEditMember(null); setShowModal(true); },
          } : undefined}
        />
      ) : (
        <div data-testid="members-list" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sorted.map(m => {
            const isSelected = selectedIds.has(m.id);
            const toggle = () => {
              setSelectedIds(prev => {
                const n = new Set(prev);
                if (n.has(m.id)) n.delete(m.id); else n.add(m.id);
                return n;
              });
            };
            return (
              <FlockMemberCard
                key={m.id}
                member={m}
                viewerRole={currentUser?.role}
                onOpen={() => {
                  if (selectMode && !m.masked) toggle();
                  else openMember(m.id);
                }}
                selectMode={selectMode && !m.masked}
                selected={isSelected}
                onToggleSelect={toggle}
              />
            );
          })}
        </div>
      )}
      </>
      )}
      {/* Iter 86 — sticky bulk-transfer bar (SA only, selection mode) */}
      {isSuperAdmin && selectMode && (
        <div
          data-testid="members-select-bar"
          style={{
            position: "fixed",
            left: 0, right: 0,
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 60px)",
            padding: "10px 16px",
            background: "var(--kc-surface)",
            borderTop: "1px solid var(--kc-border)",
            boxShadow: "0 -8px 24px rgba(0,0,0,0.20)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            zIndex: 200,
          }}
        >
          <span style={{ fontFamily: "'Cinzel',serif", fontSize: 13, fontWeight: 700, color: "var(--kc-text)", letterSpacing: "0.06em" }}>
            {selectedIds.size} selected
          </span>
          <span style={{ flex: 1 }} />
          <Button
            variant="secondary"
            size="sm"
            data-testid="members-select-cancel"
            onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="members-transfer-btn"
            disabled={selectedIds.size === 0}
            onClick={() => setTransferOpen(true)}
          >
            <CustomIcon name="chevron" size={12} style={{ marginRight: 4 }} /> Transfer
          </Button>
        </div>
      )}
      {isSuperAdmin && transferOpen && (
        <TransferModal
          sourceGroup={groupTab}
          selectedMembers={members.filter(m => selectedIds.has(m.id))}
          busy={transferBusy}
          onCancel={() => setTransferOpen(false)}
          onConfirm={async (destStatus) => {
            setTransferBusy(true);
            try {
              const res = await kcFetch(`${API_BASE}/members/bulk-transfer`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({
                  memberIds: Array.from(selectedIds),
                  destinationStatus: destStatus,
                  performedBy: currentUser?.id,
                }),
              });
              if (!res.ok) throw new Error(((await res.json().catch(() => ({}))).detail) || "Transfer failed");
              const json = await res.json();
              // Optimistically patch memberStatus on local list.
              const idSet = new Set((json.details || []).map(d => d.memberId));
              setMembers(prev => prev.map(m => idSet.has(m.id) ? { ...m, memberStatus: destStatus } : m));
              const groupLabel = destStatus === "New Member" ? "Members" : (destStatus + "s");
              showToast(`${json.transferred} ${json.transferred === 1 ? "person" : "people"} moved to ${groupLabel}`);
              setTransferOpen(false);
              setSelectMode(false);
              setSelectedIds(new Set());
              // Tree placement prompt only when the destination is a member status.
              if (destStatus === "New Member") {
                setTreePlacementQueue(Array.from(idSet));
              }
            } catch (e) {
              alert(e.message || "Transfer failed");
            } finally {
              setTransferBusy(false);
            }
          }}
        />
      )}
      {treePlacementQueue.length > 0 && (
        <TreePlacementPrompt
          count={treePlacementQueue.length}
          onSkip={() => setTreePlacementQueue([])}
          onPlace={() => {
            // Route the admin to the Follow-Up tree page — the existing
            // AssignMemberModal on the tree admin view lets them place each
            // member into a vacant slot at their own pace. We don't force
            // sequential auto-open here because the tree slot picker needs
            // a node context that this list page doesn't own.
            setTreePlacementQueue([]);
            reactNav("/followuptree");
          }}
        />
      )}
      {toast && (
        <div
          data-testid="members-toast"
          style={{
            position: "fixed",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 140px)",
            left: "50%", transform: "translateX(-50%)",
            background: "var(--kc-text)", color: "var(--kc-surface)",
            padding: "10px 18px", borderRadius: 999,
            fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700,
            boxShadow: "0 4px 16px rgba(0,0,0,0.28)",
            zIndex: 400,
          }}
        >
          {toast}
        </div>
      )}
      {showModal&&<MemberModal member={editMember} members={members} families={families} departments={departments} currentUser={currentUser} onSave={saveMember} onClose={()=>{setShowModal(false);setEditMember(null);}}/>}
    </div>
  );
}

// InfoItem imported from ../shared/InfoItem

// ============================================================
// MEMBER MODAL
// ============================================================
function MemberModal({ member, members, families, departments, currentUser, onSave, onClose }) {
  const blank = { firstName:"",lastName:"",dob:"",gender:"Male",maritalStatus:"Single",phone:[{label:"Mobile",number:""}],email:[{label:"Personal",address:""}],address:"",language:"English",baptized:false,baptizedBy:"",baptizedWhere:"",baptizedDate:"",spiritualBackground:"Raised in church",departments:[],joinedDate:"",memberStatus:"New Member",additionType:"Joined by decision",additionDate:"",additionComment:"",subtractionType:"",subtractionDate:"",subtractionComment:"",tags:[],invitedBy:"",familyId:"",photo:null, privacy: { ...PRIVACY_DEFAULTS } };
  const [form, setForm] = useState(member||blank);
  const [tab, setTab] = useState("personal");
  const isAdmin = currentUser && (currentUser.role === "superadmin" || currentUser.role === "limited_admin");
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleDept = (d) => set("departments", form.departments.includes(d)?form.departments.filter(x=>x!==d):[...form.departments,d]);
  const toggleTag = (t) => set("tags", (form.tags||[]).includes(t)?form.tags.filter(x=>x!==t):[...(form.tags||[]),t]);

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{member?"Edit Member":"Add New Member"}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{borderBottom:"1px solid var(--cream2)"}}>
          <div className="tabs" style={{padding:"0 22px",marginBottom:0}}>
            {["personal","contact","spiritual","church"].map(t=><button key={t} className={`tab ${tab===t?"active":""}`} onClick={()=>setTab(t)}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>)}
          </div>
        </div>
        <div className="modal-body">
          {tab==="personal"&&(
            <div>
              <div className="form-group full mb-14">
                <label>Profile Photo</label>
                <PhotoUploader value={form.photo} name={`${form.firstName||""} ${form.lastName||""}`.trim()} onChange={(v)=>set("photo",v)} data-testid="member-form-photo" />
              </div>
              <div className="form-grid">
              <div className="form-group"><label>First Name *</label><input value={form.firstName} onChange={e=>set("firstName",e.target.value)}/></div>
              <div className="form-group"><label>Last Name *</label><input value={form.lastName} onChange={e=>set("lastName",e.target.value)}/></div>
              <div className="form-group"><label>Date of Birth</label><input type="date" value={form.dob} onChange={e=>set("dob",e.target.value)}/></div>
              <div className="form-group"><label>Gender</label><select value={form.gender} onChange={e=>set("gender",e.target.value)}><option>Male</option><option>Female</option><option>Other</option></select></div>
              <div className="form-group"><label>Marital Status</label><select value={form.maritalStatus} onChange={e=>set("maritalStatus",e.target.value)}><option>Single</option><option>Married</option><option>Widowed</option><option>Divorced</option></select></div>
              <div className="form-group"><label>Language</label><input type="text" value={form.language} onChange={e=>set("language",e.target.value)} placeholder="e.g. English, Twi, French…" data-testid="member-form-language"/></div>
              <div className="form-group"><label>Member Status</label><select value={form.memberStatus} onChange={e=>set("memberStatus",e.target.value)}><option>Active</option><option>New Member</option><option>Visitor</option><option>Guest</option><option>Inactive</option><option>Deceased</option><option>Left Church</option></select></div>
              <div className="form-group"><label>Joined Date</label><input type="date" value={form.joinedDate} onChange={e=>set("joinedDate",e.target.value)}/></div>
              </div>

              {/* Iter-57: read-only Member Number reference. Numbers are minted
                  at tree-placement time by POST /followuptree/assign and are
                  never editable from the profile form. */}
              <div
                className="form-group full mb-14"
                data-testid="member-form-number-group"
                style={{ padding: "10px 14px", background: "color-mix(in srgb, var(--kc-accent) 4%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", borderRadius: 10 }}
              >
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <CustomIcon name="lock" size={12} /> Member Number
                </label>
                <input
                  type="text"
                  value={form.memberNumber ? `#${String(form.memberNumber).replace(/^#/, "").padStart(7, "0")}` : ""}
                  readOnly
                  data-testid="member-form-number-input"
                  placeholder="Not yet assigned"
                  style={{ opacity: 0.85, cursor: "not-allowed" }}
                />
                <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 4, fontFamily: "'Montserrat',sans-serif" }}>
                  {form.memberNumber
                    ? "Assigned by the Follow-Up tree at placement time. Numbers travel with the person."
                    : "Not yet assigned — will be assigned upon tree placement."}
                </div>
              </div>

              {/* Profession — text fields used elsewhere; rendered in form for completeness when admin edits. */}
              <div className="form-grid mb-14">
                <div className="form-group"><label>Profession</label><input value={form.profession || ""} onChange={e=>set("profession",e.target.value)} data-testid="member-form-profession" placeholder="e.g. Teacher, Engineer"/></div>
                <div className="form-group"><label>Profession Details</label><input value={form.professionDetails || ""} onChange={e=>set("professionDetails",e.target.value)} data-testid="member-form-profession-details" placeholder="Company, role, etc."/></div>
              </div>

              {/* Privacy controls — admins editing any member, owner editing themselves
                  (via My Profile). Always editable here because the form is admin-only
                  to enter. */}
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)" }}>
                <MemberPrivacyEditor
                  value={form.privacy}
                  onChange={(p) => set("privacy", p)}
                  isAdmin={!!isAdmin}
                  canEdit={true}
                  testIdPrefix="member-form-privacy"
                />
              </div>
            </div>
          )}
          {tab==="contact"&&(
            <div>
              <div className="form-section-title mb-12">Phone Numbers</div>
              {form.phone.map((p,i)=>(
                <div key={`phone-${i}`} className="flex gap-8 mb-8">
                  <select value={p.label} onChange={e=>{const ph=[...form.phone];ph[i]={...ph[i],label:e.target.value};set("phone",ph);}} style={{width:120}}><option>Mobile</option><option>Home</option><option>Work</option></select>
                  <input value={p.number} onChange={e=>{const ph=[...form.phone];ph[i]={...ph[i],number:e.target.value};set("phone",ph);}} placeholder="+1 555-0000"/>
                  {form.phone.length>1&&<button className="btn btn-danger btn-sm btn-icon" onClick={()=>set("phone",form.phone.filter((_,j)=>j!==i))}>×</button>}
                </div>
              ))}
              <button className="btn btn-outline btn-sm mb-16" onClick={()=>set("phone",[...form.phone,{label:"Mobile",number:""}])}>+ Add Phone</button>
              <div className="form-section-title mb-12">Email Addresses</div>
              {form.email.map((e,i)=>(
                <div key={`email-${i}`} className="flex gap-8 mb-8">
                  <select value={e.label} onChange={ev=>{const em=[...form.email];em[i]={...em[i],label:ev.target.value};set("email",em);}} style={{width:120}}><option>Personal</option><option>Work</option><option>Other</option></select>
                  <input value={e.address} onChange={ev=>{const em=[...form.email];em[i]={...em[i],address:ev.target.value};set("email",em);}} placeholder="email@example.com"/>
                  {form.email.length>1&&<button className="btn btn-danger btn-sm btn-icon" onClick={()=>set("email",form.email.filter((_,j)=>j!==i))}>×</button>}
                </div>
              ))}
              <button className="btn btn-outline btn-sm mb-16" onClick={()=>set("email",[...form.email,{label:"Personal",address:""}])}>+ Add Email</button>
              <div className="form-group full"><label>Home Address</label><input value={form.address} onChange={e=>set("address",e.target.value)} placeholder="Street, City, Country"/></div>
            </div>
          )}
          {tab==="spiritual"&&(
            <div>
              <div className="form-grid mb-16">
                <div className="form-group"><label>Baptized?</label><select value={form.baptized?"Yes":"No"} onChange={e=>set("baptized",e.target.value==="Yes")}><option>No</option><option>Yes</option></select></div>
                <div className="form-group"><label>Spiritual Background</label><select value={form.spiritualBackground} onChange={e=>set("spiritualBackground",e.target.value)}><option>Raised in church</option><option>New to faith</option><option>Different denomination</option><option>Different religion</option><option>No background</option></select></div>
              </div>
              {form.baptized&&(
                <div className="form-grid mb-16">
                  <div className="form-group"><label>Baptized By</label><input value={form.baptizedBy} onChange={e=>set("baptizedBy",e.target.value)}/></div>
                  <div className="form-group"><label>Where</label><input value={form.baptizedWhere} onChange={e=>set("baptizedWhere",e.target.value)}/></div>
                  <div className="form-group"><label>Baptism Date</label><input type="date" value={form.baptizedDate} onChange={e=>set("baptizedDate",e.target.value)}/></div>
                </div>
              )}
              <div className="form-section-title mb-12">Membership Changes</div>
              <div className="form-grid mb-12">
                <div className="form-group"><label>Addition Type</label><select value={form.additionType} onChange={e=>set("additionType",e.target.value)}><option>Joined by decision</option><option>Transfer from another church</option><option>Newborn/Dedication</option><option>Other</option></select></div>
                <div className="form-group"><label>Addition Date</label><input type="date" value={form.additionDate} onChange={e=>set("additionDate",e.target.value)}/></div>
              </div>
              <div className="form-group full mb-12"><label>Addition Notes</label><textarea value={form.additionComment} onChange={e=>set("additionComment",e.target.value)}/></div>
              <div className="form-grid mb-12">
                <div className="form-group"><label>Left / Removed</label><select value={form.subtractionType} onChange={e=>set("subtractionType",e.target.value)}><option value="">None</option><option>Death</option><option>Left voluntarily</option><option>Transferred</option><option>Excommunicated</option><option>Other</option></select></div>
                {form.subtractionType&&<div className="form-group"><label>Date</label><input type="date" value={form.subtractionDate} onChange={e=>set("subtractionDate",e.target.value)}/></div>}
              </div>
              {form.subtractionType&&<div className="form-group full"><label>Departure Notes</label><textarea value={form.subtractionComment} onChange={e=>set("subtractionComment",e.target.value)}/></div>}
            </div>
          )}
          {tab==="church"&&(
            <div>
              <div className="form-grid mb-16">
                <div className="form-group"><label>Family Unit</label><select value={form.familyId||""} onChange={e=>set("familyId",e.target.value)}><option value="">None</option>{families.map(f=><option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
                <div className="form-group"><label>Invited By</label><select value={form.invitedBy||""} onChange={e=>set("invitedBy",e.target.value)}><option value="">Unknown</option>{members.map(m=><option key={m.id} value={m.id}>{fullName(m)}</option>)}</select></div>
              </div>
              <div className="form-section-title mb-12">Departments</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:18}}>
                {departments.map(d=><button key={d} className={`btn btn-sm ${form.departments.includes(d)?"btn-primary":"btn-outline"}`} onClick={()=>toggleDept(d)}>{d}</button>)}
              </div>
              <div className="form-section-title mb-12">Tags</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {["Single","Married","Widowed","Divorced","Youth","Young Adult","Adult","Senior","Elder","Deacon","Team Leader","Leadership","New to faith","Growing","Mature believer"].map(t=><button key={t} className={`btn btn-sm ${(form.tags||[]).includes(t)?"btn-gold":"btn-outline"}`} onClick={()=>toggleTag(t)}>{t}</button>)}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-gold"
            data-testid="member-form-save"
            onClick={()=>{
              if(!form.firstName||!form.lastName){alert("First and last name are required.");return;}
              onSave(form);
            }}
          >Save Member</button>
        </div>
      </div>
    </div>
  );
}


export { MembersPage };

// Iter-92 — the following components were extracted into their own files:
// FlockToolbarMenu, FlockGroupTabs, TransferModal, TreePlacementPrompt,
// FlockFilterPills, FlockMemberCard (see sibling files in this directory).
